import type {FC, PropsWithChildren} from 'react';
import React, {createContext, useCallback, useContext, useMemo, useState} from 'react';

/*
 * Props.
 */

export interface HighlightsCoordinatorProps {
  addRefreshCb: (key: string, fn: () => void) => void;
  removeRefreshCb: (key: string) => void;
  refresh: () => void;
}

/*
 * Context.
 */

/**
 * Context to coordinate the refresh of the highlights.
 * Components that want to refresh the highlights should use the `useHighlightsCoordinator` hook.
 * Components that imlement highlights should use the addRefreshCb and removeRefreshCb functions to register and unregister their refresh callbacks.
 */
export const HighlightsCoordinatorContext = createContext<HighlightsCoordinatorProps | null>(null);

export function useHighlightsCoordinator() {
  return useContext(HighlightsCoordinatorContext);
}

export const HighlightsCoordinatorProvider: FC<PropsWithChildren> = ({children}) => {
  const [refreshFns, setRefreshFns] = useState<ReadonlyArray<{key: string; cb: () => void}>>([]);

  const addRefreshCb = useCallback(
    (key: string, cb: () => void) => {
      setRefreshFns((currentFns) => [...currentFns.filter((e) => e.key !== key), {key, cb}]);
    },
    [setRefreshFns],
  );

  const removeRefreshCb = useCallback(
    (key: string) => {
      setRefreshFns((currentFns) => [...currentFns.filter((e) => e.key !== key)]);
    },
    [setRefreshFns],
  );

  const refresh = useCallback(() => {
    refreshFns.forEach((fn) => fn.cb());
  }, [refreshFns]);

  const context = useMemo(
    () => ({addRefreshCb, refresh, removeRefreshCb}),
    [addRefreshCb, refresh, removeRefreshCb],
  );

  return (
    <HighlightsCoordinatorContext.Provider value={context}>{children}</HighlightsCoordinatorContext.Provider>
  );
};
